/**
 * Drill Tag Editor
 * Panel for editing tags on a single drill
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Save, Tag as TagIcon } from 'lucide-react';
import { UnifiedTag } from './UnifiedTag';
import { Drill } from './types/drill-types';
import { loadTaxonomy } from '@/lib/taxonomy-loader';

type TagMap = { [key: string]: string[] };

interface DrillTagEditorProps {
  drill: Drill;
  isOpen: boolean;
  onClose: () => void;
  onSave: (drillId: string, updatedTags: TagMap) => void;
}

const normalizeTags = (tags: { [key: string]: string | string[] } = {}): TagMap => {
  const normalized: TagMap = {};
  Object.entries(tags).forEach(([category, value]) => {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    if (values.length > 0) {
      normalized[category] = Array.from(new Set(values));
    }
  });
  return normalized;
};

export function DrillTagEditor({ drill, isOpen, onClose, onSave }: DrillTagEditorProps) {
  const [editedTags, setEditedTags] = useState<TagMap>(() => normalizeTags(drill.tags));
  const [taxonomy, setTaxonomy] = useState<{ [key: string]: { type: string; options: string[] } }>({});

  useEffect(() => {
    const initializeTaxonomy = async () => {
      const data = await loadTaxonomy();
      if (data.categories) {
        setTaxonomy(data.categories);
      }
    };
    initializeTaxonomy();
  }, []);

  useEffect(() => {
    setEditedTags(normalizeTags(drill.tags));
  }, [drill.tags]);

  const handleSave = () => {
    onSave(drill.id, editedTags);
    onClose();
  };

  const handleToggleTag = (category: string, option: string) => {
    const currentValues = editedTags[category] || [];
    const isSelected = currentValues.includes(option);
    const nextValues = isSelected
      ? currentValues.filter(v => v !== option)
      : [...currentValues, option];

    if (nextValues.length === 0) {
      const newTags = { ...editedTags };
      delete newTags[category];
      setEditedTags(newTags);
    } else {
      setEditedTags({
        ...editedTags,
        [category]: Array.from(new Set(nextValues))
      });
    }
  };

  const handleRemoveTag = (category: string, optionToRemove?: string) => {
    const currentValue = editedTags[category] || [];

    if (!optionToRemove) {
      // Remove entire category
      const newTags = { ...editedTags };
      delete newTags[category];
      setEditedTags(newTags);
    } else {
      // Remove specific option from multi-select
      const newValues = currentValue.filter(v => v !== optionToRemove);
      if (newValues.length === 0) {
        const newTags = { ...editedTags };
        delete newTags[category];
        setEditedTags(newTags);
      } else {
        setEditedTags({
          ...editedTags,
          [category]: Array.from(new Set(newValues))
        });
      }
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-bg/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 flex-col">
      <Card className="w-full max-w-2xl flex flex-col gap-0" style={{ maxHeight: '85vh' }}>
        <CardHeader className="pb-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TagIcon className="w-5 h-5" />
              <CardTitle className="text-lg text-accent">Edit Tags - {drill.name}</CardTitle>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} size="sm" className="h-10">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button onClick={onClose} variant="ghost" size="sm" className="h-10">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden p-6 flex flex-col">
          <ScrollArea className="flex-1 w-full">
            <div className="space-y-6 pr-4">
              {/* Current Tags Display */}
              {Object.keys(editedTags).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Current Tags</Label>
                  <div className="flex flex-wrap gap-2 p-3 bg-surface/30 rounded-lg border border-line">
                    {Object.entries(editedTags).map(([category, value]) => {
                      const values = Array.isArray(value) ? value : [value];
                      return values.map((val, idx) => (
                        <UnifiedTag
                          key={`${category}-${val}-${idx}`}
                          category={category}
                          value={val}
                          onClick={() => handleRemoveTag(category, val)}
                          size="sm"
                        />
                      ));
                    })}
                  </div>
                </div>
              )}

              {/* Tag Editors by Category */}
              <div className="space-y-4">
                <Label className="text-sm font-semibold">Add/Modify Tags</Label>
                {Object.entries(taxonomy).map(([category, config]) => {
                  if (config.type === 'text') return null;

                  const selectedValues = editedTags[category] || [];

                  return (
                    <div key={category} className="space-y-2 p-4 bg-surface/20 border border-line rounded-lg">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">{category}</Label>
                        {selectedValues.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveTag(category)}
                            className="h-7 text-xs"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 p-2 bg-bg rounded border border-line">
                        {config.options && config.options.length > 0 ? (
                          config.options.map(option => {
                            const isSelected = selectedValues.includes(option);

                            return (
                              <UnifiedTag
                                key={option}
                                category={category}
                                value={option}
                                onClick={() => {
                                  handleToggleTag(category, option);
                                }}
                                isActive={isSelected}
                                size="sm"
                              />
                            );
                          })
                        ) : (
                          <p className="text-xs text-muted">No options available</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
}

